/* eslint-disable @typescript-eslint/no-unused-vars */
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import * as tus from 'tus-js-client';
import mediaService from '../services/media-service';
import { MediaObject } from '../types/media-types';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from 'src/components/ui/use-toast';
import { uniqBy } from 'lodash';
import { initDB, storeFile, getFile, deleteFile } from '../../../shared/services/indexedDBService';
import { useBrandSelection } from 'src/providers/BrandSelectionProvider';
import { Logger } from 'src/utils/logger';
import {
  isDeadUploadUrl,
  describeRefusedProbe,
  readUploadFailureShape,
  applyResumePersistence,
  mergePersistedUploadsForWrite,
  resumeBrandScope,
  resumeStartProgress,
  createDeadRecordDiscardGate,
  serialiseOwnRows,
} from '../services/upload-resume.logic';
import { wait } from 'src/lib/utils/wait';
import { MAX_SINGLE_FILE_SIZE } from 'src/components/Form/useUploadMiddleware';
// Import directly from the store file to avoid a circular import chain via the
// background-upload barrel (BackgroundUploadOrchestrator imports MediaUploadContext).
import { useBackgroundUploadStore } from 'src/features/requests/background-upload/use-background-upload-store';
import { Api } from 'src/api';

/**
 * Calculate optimal chunk size based on file size
 * Uses larger chunks for larger files to reduce HTTP overhead
 *
 * @param fileSize - Size of file in bytes
 * @returns Optimal chunk size in bytes
 */
const calculateOptimalChunkSize = (fileSize: number): number => {
  const MB = 1024 * 1024;

  // For files < 50MB: use 6MB chunks
  if (fileSize < 50 * MB) {
    return 6 * MB;
  }

  // For files 50MB - 500MB: use 10MB chunks
  if (fileSize < 500 * MB) {
    return 10 * MB;
  }

  // For files 500MB - 2GB: use 20MB chunks
  if (fileSize < 2000 * MB) {
    return 20 * MB;
  }

  // For files > 2GB: use 50MB chunks
  return 50 * MB;
};

// define types for media upload queue
export interface UploadItem {
  /**
   * Client identity. Never changes once assigned.
   *
   * Joins the whole upload subsystem: uploadItems, the `currentUploads` registry, the IndexedDB
   * blob key, the tus fingerprint, persistedUploads, and — outside this provider —
   * `PendingUpload.tempId`, which is never remapped. Re-keying it detaches all of them at once.
   */
  id?: string;
  tempId: string; // local temporal id before get id from server
  /**
   * Server identity: the `media` row being written to, when it differs from `id`.
   *
   * Set only when a resume was answered with a fresh record — past the pending-record window the
   * backend deletes the old row and inserts a new one. Used for server lookups only.
   */
  serverId?: string;
  fileName: string;
  fileSize: number;
  file: File;
  progress: number;
  status: 'pending' | 'in-progress' | 'uploaded' | 'failed';
  uploadUrl: string;
  uploadToken: string;
  path: string[];
  error?: string; // To store error message on failure
}

export interface PersistedUpload {
  /** Client identity — stable for the life of the record. See `UploadItem.id`. */
  id: string;
  /** Server identity when a resume was restarted onto a new media row. See `UploadItem.serverId`. */
  serverId?: string;
  fileName: string;
  fileSize: number;
  progress: number;
  uploadUrl: string;
  uploadToken: string;
  path: string[];
  fileType: string;
  /**
   * The brand the upload belonged to when it started. Optional because entries persisted before
   * this field existed will not have it.
   *
   * Resume re-requests an upload URL, and the backend scopes its pending-record lookup by
   * user_id (the brand). Without this the request was scoped to whichever brand happens to be
   * active at resume time, so on a multi-brand account switching brands before resuming attributed
   * the media to the wrong brand or had it rejected.
   */
  brandId?: string;
}

/**
 * What a resume entry point actually did — the third outcome, alongside `onSuccess` and `onError`.
 *
 * `'refused'` means admission control handed the record to another in-flight attempt and NEITHER
 * callback will ever fire. It is deliberately not an error: reporting a refusal through `onError`
 * flipped a healthy in-flight upload to 'failed', because both automatic callers treat onError as
 * terminal (see `acquireResumeLock`). But silence was not free either — a caller that allocated
 * something per click had no signal to release it, so a refusal leaked a file input that could
 * never fire `change` again, and a retry slot that could never be won back.
 *
 * So: callbacks report the upload, the return value reports the attempt.
 *
 * `'attempted'` covers every path that resolves through a callback — started, cancelled mid-flight,
 * file missing from cache, bad arguments. Callers only ever need to branch on `'refused'`.
 */
export type ResumeOutcome = 'attempted' | 'refused';

interface MediaUploadContextType {
  uploadItems: UploadItem[];
  persistedUploads: PersistedUpload[];
  validResumableUploads: () => PersistedUpload[];
  isUploading: boolean;
  handleUpload: (params: {
    files: File[];
    userPath: string[];
    onSuccess: (items: UploadItem[]) => void;
    onError: (error: any) => void;
    onSingleFileError: (item: UploadItem, error: any) => void;
    onSingleFileSuccess: (item: UploadItem, mediaObject: MediaObject) => void;
    brandId?: string; // Optional brandId override for WebView uploads
  }) => Promise<void>;
  stopUpload: (id: string) => Promise<void>;
  uploadCanBeCancelled: (id: string) => boolean;
  removePersistedUpload: (id: string) => Promise<void>;
  /** True when another attempt already owns this record, so a resume would be refused. */
  isResumeInFlight: (id: string) => boolean;
  currentUploads: Record<string, tus.Upload>;
  resumeUpload: (params: {
    persistedUpload: PersistedUpload;
    onSuccess: (item: UploadItem, mediaObject: MediaObject) => void;
    onError: (error: any) => void;
  }) => Promise<ResumeOutcome>;
  resumeUploadWithFile: (params: {
    file: File;
    persistedUpload: PersistedUpload;
    onSuccess: (item: UploadItem, mediaObject: MediaObject) => void;
    onError: (error: any) => void;
  }) => Promise<ResumeOutcome>;
}

// Create the context with a default value
export const MediaUploadContext = createContext<MediaUploadContextType | undefined>(undefined);

// Single source of truth for the persisted-uploads localStorage key. Exported so
// background-upload code paths that run *outside* MediaUploadProvider can read
// the same store without the constant drifting across files.
export const PERSISTED_UPLOADS_KEY = 'beCreatives_persisted_uploads';

// Custom event broadcast when a persisted-upload entry is removed via the
// localStorage-direct path (UploadingStatusCell renders on the dashboard /
// requests-list, neither of which is inside this provider). MediaUploadProvider
// listens so its in-memory `persistedUploads` mirror drops the entry — without
// this, the provider's setPersistedUploads-on-progress effect would re-write
// the entry back to localStorage on the next progress tick from any concurrent
// upload, restoring the metadata the user just dismissed.
export const PERSISTED_UPLOAD_REMOVED_EVENT = 'becreatives:persisted-upload-removed';

/**
 * Why a persisted row went away. The two cases need DIFFERENT handling, and conflating them is a
 * bug in each direction.
 *
 * `'dismissed'` — the user removed it. Sets the cancel flag, so an in-flight resume that is still
 * awaiting a network round-trip abandons instead of uploading a file the user removed.
 *
 * `'completed'` — the upload succeeded and cleaned up after itself. Siblings must drop their mirror
 * copy (or they resurrect a record whose IndexedDB blob is already deleted, which then fails
 * "File not found in local cache" forever), but the cancel flag must NOT be set: this id was never
 * cancelled, and marking it so would make any later reader believe the user had removed it.
 */
export type PersistedUploadRemovedReason = 'dismissed' | 'completed';

/**
 * What a `'completed'` broadcast carries beyond the id.
 *
 * Enough for a listener to run the SAME success side-effects the owning caller runs — specifically
 * the metadata prompt, which needs the media row itself and the folder the file went to, and which a
 * listener has no other way to obtain for an upload it did not start.
 *
 * `media` is null when tus finished but the post-upload verification timed out: the record is gone
 * from the persisted list either way, there is simply nothing to describe.
 */
export interface PersistedUploadCompletion {
  media: MediaObject | null;
  /** The folder the file was uploaded into. Empty means the library root. */
  path: string[];
}

/**
 * The `detail` of a PERSISTED_UPLOAD_REMOVED_EVENT.
 *
 * Exported so listeners share one declaration rather than each hand-rolling the shape at its
 * `CustomEvent<...>` cast — two copies of it had already drifted apart on the `reason` field. Every
 * member is optional because the out-of-provider door in `persisted-uploads-storage` predates both
 * `reason` and the completion payload and still dispatches a bare `{ id }`.
 */
export interface PersistedUploadRemovedDetail extends Partial<PersistedUploadCompletion> {
  id?: string;
  reason?: PersistedUploadRemovedReason;
}

/**
 * Announce a dismissal to every OTHER mounted provider.
 *
 * `MediaUploadProvider` is mounted in six places, and each instance mirrors the persisted list in
 * React state and rewrites its whole array to localStorage on the next progress tick. So a dismissal
 * that only updated the acting instance was undone by any sibling: the row came back in localStorage
 * and in "Resumable Uploads", with its IndexedDB blob already deleted — a record that can never
 * succeed and can never be resumed.
 *
 * `removePersistedUploadStorage` already did this for the out-of-provider door; the two in-provider
 * doors did not, which is why they are wired here rather than at each call site.
 *
 * The acting provider receives its own event. That is harmless — every step of the handler is
 * idempotent — and it keeps this a plain broadcast rather than a broadcast-to-others.
 */
const announcePersistedUploadRemoved = (
  id: string,
  reason: PersistedUploadRemovedReason = 'dismissed',
  completion?: PersistedUploadCompletion,
) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(PERSISTED_UPLOAD_REMOVED_EVENT, { detail: { id, reason, ...completion } }),
  );
};

/**
 * Ids the user has cancelled or dismissed.
 *
 * `stopUpload` can only abort an upload already registered in `currentUploads`, but a resume awaits
 * a network round-trip before it registers anything. A cancel landing in that window did nothing,
 * and the resume then uploaded a file the user had removed. Read after awaits, so not React state.
 *
 * Module scope because `MediaUploadProvider` is mounted in six places while an upload record has one
 * id in localStorage — per-instance, a Remove in one provider was invisible to a resume in another.
 */
const cancelledUploadIds = new Set<string>();

/** Records with a resume attempt in flight. Module scope for the same reason. */
const resumingUploadIds = new Set<string>();

/**
 * Records whose dead pending row is being discarded right now.
 *
 * Module scope for the same reason as the two sets above — the discard can be started by whichever
 * provider hit the failure, and the next Resume can be pressed in a different one. See the gate's own
 * docs for the race: the discard is what makes the next press work, so the next press has to wait for
 * it. Bounded, so a hung cancel can never make Resume itself the dead end.
 */
const deadRecordDiscardGate = createDeadRecordDiscardGate();

/**
 * Records with a LIVE tus transfer, in any mounted provider.
 *
 * The companion to `resumingUploadIds`, and the reason admission control finally holds across
 * instances. `resumingUploadIds` is released the moment `upload.start()` is called, because the
 * transfer then runs for minutes; from that point on "is someone already uploading this?" was
 * answered from `currentUploads`, a PER-INSTANCE ref. So with two providers mounted at once — the
 * Revisions page nests one inside another (RevisionsPage → revision-chat-content), and the
 * media-library page coexists with the request wizard's — provider B saw an empty registry, granted
 * itself a lock, and started a SECOND `tus.Upload` PATCHing the same resource. That corrupts
 * server-side offset accounting, and `stopUpload` can only ever abort one of the two.
 *
 * Ids only, unlike `currentUploads`: a `tus.Upload`'s callbacks are bound to one provider's setState,
 * so the object itself must not be shared. Cancelling still goes through the owning instance.
 *
 * Released by the END OF THE TRANSFER, never by the unmount of the provider that started it. The tus
 * callbacks that call `unregisterUpload` are bound to module state, so they still run for an upload
 * whose provider has gone — and the upload itself keeps running, which is how an upload survives
 * navigating away. Releasing on unmount instead published "this record is free" while it was still
 * being written, which let a second `tus.Upload` start against it; the provider has a note where that
 * unmount effect used to be, covering why removing it is safe and why aborting instead is not.
 */
const activeUploadIds = new Set<string>();

/**
 * The live `tus.Upload` objects themselves, by id — so ANY mounted provider can abort one.
 *
 * The companion to `activeUploadIds`, and the thing that makes "cancel" honest across instances.
 * `currentUploads` is a per-instance ref, so only the provider that STARTED a transfer could ever
 * abort it. Every dismissal path documented itself as safe on the grounds that a sibling's transfer
 * "will see the cancel flag and stop of its own accord" — which is not true and never was: nothing
 * inside the tus callbacks reads `cancelledUploadIds`. So a Remove taken from the dashboard cell, or
 * from any provider other than the owner, set the flag, dropped the row and deleted the IndexedDB
 * blob while the bytes kept going — and the transfer then ran to completion, published itself through
 * `recordCompletedUpload`, and fired the completion broadcast, which now pops an "Edit Info" dialog
 * for a file the user explicitly removed.
 *
 * It also gives a REMOUNTED provider a handle on a transfer it did not start. A page-level provider
 * unmounts on navigation while its `tus.Upload` keeps running (see the note where the unmount effect
 * used to be); without this, coming back to the page left an upload that could be neither resumed —
 * correctly, it is already running — nor cancelled, because `uploadCanBeCancelled` consulted a
 * registry that had gone away with the old instance.
 *
 * Holding the object here is safe in a way that SHARING its callbacks would not be: `abort()` is a
 * transport operation on tus's own XHR and needs nothing from the owning provider's setState. The
 * per-instance registry stays for the context value and for the owner's own bookkeeping.
 *
 * WHAT IT RETAINS, and why that is bounded. Unlike `activeUploadIds`, whose residue is one string,
 * an entry here pins the `tus.Upload` — and with it the `File` and the callbacks created in
 * `createUploadFromUploadItem`, which close over the provider that made them. So an entry that
 * outlived its transfer would keep a dead React provider uncollectable. Every terminal path removes
 * the entry — `onSuccess`, `onError`, and all three dismiss paths, via `unregisterUpload` — so what
 * is left is a transfer that never terminates at all: a hung XHR that never errors, which is a
 * stalled upload holding its own bytes in memory regardless of this map, and was equally unbounded
 * before. A displaced entry (see `registerUpload`) is the other case, and is logged rather than
 * cleaned up, because every way of cleaning it up costs more than it saves.
 */
const liveUploads = new Map<string, tus.Upload>();

// Provider component
export const MediaUploadProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { toast } = useToast();

  // Get selected brand for upload API calls
  const { selectedBrand } = useBrandSelection();

  const [isUploading, setIsUploading] = useState(false);
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);
  const [persistedUploads, setPersistedUploads] = useState<PersistedUpload[]>([]);
  const isFirstLoad = useRef(true);
  // Per-instance, unlike the two module-level sets below it: the tus callbacks held here are bound
  // to THIS provider's setState. That difference is load-bearing (see acquireResumeLock's residual
  // note), so the module sets are referenced directly rather than through `useRef` wrappers that
  // made all three read as `x.current` and hid it.
  const currentUploads = useRef<Record<string, tus.Upload>>({});

  /**
   * Every id this instance has ever held in `persistedUploads`.
   *
   * Read by the localStorage write below to tell a REMOVAL apart from a row this instance simply
   * never knew about. Without that distinction the two are indistinguishable — both are "an id in
   * storage that is not in my state" — and the write had to guess.
   */
  const knownUploadIds = useRef<Set<string>>(new Set());

  /**
   * This instance's copy of each row, serialised, as of its last localStorage write.
   *
   * Lets the write below tell "I changed this row" from "I am simply holding an old copy of it". A
   * row can be changed by a SIBLING instance, and this instance would never hear about it — nothing
   * re-reads storage after mount — so without this comparison any state change here (a progress tick
   * on an unrelated upload) flushed a stale copy over a sibling's fresh `serverId` and upload URL.
   */
  const lastWrittenOwnRows = useRef<Map<string, string>>(new Map());

  /** Register a live tus upload: locally, for cancelling, and globally, for admission control. */
  const registerUpload = (id: string, upload: tus.Upload) => {
    // An existing module entry means a SECOND transfer is starting for a record that already has
    // one. It should not happen — `acquireResumeLock` refuses it — but `doUpload` has no
    // cross-instance admission control, and the backend can answer two providers with the same record
    // for one file name + size inside the validity window. Logged loudly, and NOT aborted.
    //
    // Aborting the displaced upload was tried and is worse. `abort()` fires `onabort`, not `onerror`,
    // so neither tus callback runs and nothing calls `unregisterUpload` for it — the aborted object
    // stays in the LOSING provider's per-instance registry for good. `findLiveUpload` prefers that
    // registry, so a later Cancel there would abort the dead handle and then unconditionally
    // `unregisterUpload(id)`, clearing the claim of the transfer that is still PATCHing — reopening
    // the double-transfer window this mechanism exists to close.
    if (liveUploads.has(id) && liveUploads.get(id) !== upload) {
      Logger.warn(
        '[MediaUploadContext] A second transfer started for a record that already had one',
        'MediaUploadContext',
        { id },
      );
    }
    currentUploads.current[id] = upload;
    activeUploadIds.add(id);
    liveUploads.set(id, upload);
  };

  /**
   * The live transfer for this record, whichever instance started it.
   *
   * Prefers this instance's own registry purely so the common case reads unchanged; the module map
   * is what makes a dismissal from a non-owning provider — or from a provider that remounted after
   * the owner went away — actually stop the bytes rather than only tidying up the UI around them.
   */
  const findLiveUpload = (id: string): tus.Upload | undefined =>
    // THIS instance's own object first, module map only as a fallback — i.e. the module map is
    // consulted exactly when this instance has nothing, which is the case it was added for (a Remove
    // taken from the dashboard cell, or from a provider that never started the transfer).
    //
    // The ordering matters under a server-id collision, which the backend makes reachable: an upload
    // re-requested for the same file name + size + user inside the validity window is answered with
    // the SAME record, and `doUpload` has no cross-instance admission control, so two providers can
    // register two live transfers under one id. `liveUploads` keeps only the last of them. Preferring
    // it would make this instance abort the OTHER instance's transfer and then delete the entry,
    // leaving its own running with no handle anywhere — strictly worse than preferring the object we
    // know we own. Neither registry is authoritative under that collision; this ordering at least
    // keeps every instance able to stop what it started.
    currentUploads.current[id] ?? liveUploads.get(id);

  /**
   * Release a tus upload. Called when the transfer ends (success, failure) or is cancelled.
   *
   * Clears the module claim as well as the local entry, so a record whose upload has finished is
   * resumable again. Every caller is either the owning instance reporting the end of its own
   * transfer, or a global cancellation, so clearing the module claim is correct in both.
   */
  const unregisterUpload = (id: string) => {
    const mine = currentUploads.current[id];
    delete currentUploads.current[id];

    // IDENTITY-CHECKED before clearing the module state. If this instance holds an object for `id`
    // and it is NOT the one the module map holds, then a second transfer was registered for this
    // record after ours (see `registerUpload`) — ours is the loser, and clearing the shared claim on
    // our way out would free the claim of the transfer that is still PATCHing. That is the same harm
    // as aborting the displaced upload, just reached by waiting: the winner's record would read as
    // free, admitting a THIRD tus.Upload, and a dismissal would then find no handle at all and delete
    // the row and the blob out from under live bytes.
    //
    // The `!mine` case still clears unconditionally, and must: that is a dismissal taken from a
    // provider that never owned the transfer, which is exactly what the module registry is for.
    if (mine && liveUploads.get(id) !== mine) return;

    activeUploadIds.delete(id);
    liveUploads.delete(id);
  };

  // THERE IS DELIBERATELY NO UNMOUNT CLEANUP HERE. Nothing this instance holds is released when it
  // goes away — see `activeUploadIds`.
  //
  // An effect here used to drop every id in this instance's registry, on the reasoning that a provider
  // which goes away mid-transfer would leave its ids claimed forever. That reasoning was wrong about
  // the premise: a `tus.Upload` is not owned by the React tree. It holds its own XHR and keeps
  // PATCHing after the component that started it has gone, which is what makes an upload survive
  // navigating away from the media library at all. Releasing the claim while that transfer runs told
  // the rest of the app the record was free, so the always-mounted wizard provider
  // (`global-container` → `RequestWizard`) — or its orchestrator's mount-time auto-resume — could
  // acquire the resume lock and start a SECOND `tus.Upload` against the same resource. Two PATCH
  // streams on one upload is the exact corruption `acquireResumeLock` exists to prevent, and the
  // unmount path was handing it out.
  //
  // The claim is accurate for as long as it is held: an id is in `activeUploadIds` only while a
  // transfer is live, and every way a transfer can end releases it from the tus callbacks themselves
  // — `onSuccess` and `onError` both call `unregisterUpload`, and both still fire after unmount
  // because they close over module-level state, not over this component. The dismiss paths
  // (`stopUpload`, `removePersistedUpload`, the removed-event handler) clear it unconditionally from
  // whichever instance is still mounted. So there is no id that unmounting needs to clean up, and the
  // "claimed forever" case it was written for is a transfer that never terminates — equally possible
  // while mounted, and not something this effect ever addressed.
  //
  // Aborting the transfer here instead was the other candidate fix and is rejected on purpose: it
  // would close the race by killing every upload the moment the user navigates off the page, turning
  // a working background upload into a stalled one. That is a bigger regression than the bug.

  useEffect(() => {
    initDB().catch((error) => {
      Logger.warn('Failed to initialize IndexedDB for media uploads:', 'MediaUploadContext', error);
    });
  }, []);

  useEffect(() => {
    if (uploadItems.length === 0) {
      setIsUploading(false);
    }
  }, [uploadItems]);

  useEffect(() => {
    const loadPersistedUploads = async () => {
      const persistedData = localStorage.getItem(PERSISTED_UPLOADS_KEY);
      if (persistedData) {
        try {
          const parsed = JSON.parse(persistedData) as PersistedUpload[];
          if (Array.isArray(parsed)) {
            // Drop malformed rows HERE, once, rather than defending against them at each reader.
            // `serialiseOwnRows` and the `row?.id &&` below already skip them, but the array itself
            // went into state intact — and the write effect two blocks down then does
            // `persistedUploads.map((row) => [row.id, row])` with no guard, so a single null in
            // localStorage threw a TypeError out of an effect body and blanked the page behind the
            // nearest error boundary. `mergePersistedUploadsForWrite` and `validResumableUploads`
            // dereference `.id` just as bluntly. The list is only as well-formed as whatever last
            // wrote it, so it is filtered at the door.
            const loaded = parsed.filter((row): row is PersistedUpload => !!row?.id);
            // Seeded here, not only on write: a row loaded at mount and dismissed before the first
            // write would otherwise never be recorded as known, and the merge below would read its
            // absence as "belongs to a sibling" and faithfully preserve the row the user removed.
            loaded.forEach((row) => knownUploadIds.current.add(row.id));
            // The SAME seeding for the write-history map, and for the mirror-image reason: a loaded
            // row is one this instance is merely HOLDING, and with no entry the merge below reads it
            // as one this instance just created and keeps it over a sibling's newer copy. See
            // `serialiseOwnRows` for the write that used to destroy.
            lastWrittenOwnRows.current = serialiseOwnRows<PersistedUpload>(loaded);
            setPersistedUploads(loaded);
          }
        } catch (error) {
          console.error('Error loading persisted uploads:', error);
        }
      }
      setTimeout(() => {
        isFirstLoad.current = false;
      }, 100);
    };
    loadPersistedUploads();
  }, []);

  /**
   * Mirror this instance's rows into localStorage — by MERGE, never by overwrite.
   *
   * `localStorage.setItem(KEY, JSON.stringify(persistedUploads))` was wrong for as long as more than
   * one provider could be mounted, which is always: the Revisions page nests one provider inside
   * another, and the media-library page coexists with the request wizard's. Each instance loads
   * storage once, at mount, so its mirror can never contain a row a SIBLING created afterwards — and
   * a blind write published that ignorance, deleting the sibling's rows outright.
   *
   * The completion broadcast added in this PR turned that from a latent race into a reliable one: it
   * fires on every successful upload and its handler calls `setPersistedUploads` in EVERY mounted
   * provider, so every stale mirror got flushed to storage on every completion. An upload still in
   * flight would lose its persisted row, and then, if it failed, have no record to resume from and an
   * orphaned IndexedDB blob — precisely the unfinishable state this PR exists to remove.
   *
   * So: rows this instance holds win, rows it has never seen survive untouched, and only rows it
   * once held and has since dropped are treated as removals.
   */
  useEffect(() => {
    if (isFirstLoad.current) return;

    const mine = new Map(persistedUploads.map((row) => [row.id, row]));
    mine.forEach((_row, id) => knownUploadIds.current.add(id));

    let stored: PersistedUpload[] = [];
    try {
      const raw = localStorage.getItem(PERSISTED_UPLOADS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      // Filtered exactly as the load effect filters, and for the same reason: `stored` is fed
      // straight into `mergePersistedUploadsForWrite`, which does `stored.filter((row) =>
      // mineById.has(row.id))` with no guard of its own. Filtering only at the load left this door
      // open — anything else that writes the shared key (`persisted-uploads-storage`, or an older
      // build) can put a malformed row in front of a merge that runs on every progress tick, and the
      // resulting TypeError escapes this effect and blanks the page behind the error boundary.
      if (Array.isArray(parsed)) {
        stored = (parsed as PersistedUpload[]).filter((row): row is PersistedUpload => !!row?.id);
      }
    } catch (error) {
      // A corrupt store must not stop us writing a good one — the load effect above is equally
      // tolerant, and treating it as empty degrades to the old overwrite behaviour at worst.
      console.error('Error reading persisted uploads before write:', error);
    }

    const next = mergePersistedUploadsForWrite<PersistedUpload>({
      stored,
      mine: persistedUploads,
      known: knownUploadIds.current,
      lastWrittenOwn: lastWrittenOwnRows.current,
    });

    // AFTER a successful write, never before it. `setItem` throws — QuotaExceededError, or a
    // SecurityError in Safari private mode — and snapshotting first recorded a write that did not
    // happen. The next pass then found our copy unchanged since that phantom write and storage still
    // holding the pre-write values, concluded from the mismatch that a SIBLING had moved the row, and
    // deferred to it — silently dropping the fresh serverId / uploadUrl / progress this instance had
    // just learned by resuming. That is precisely the lost write this merge exists to prevent,
    // reintroduced through the failure path.
    //
    // Wrapped for the same reason the read above is, and the same reason
    // `persisted-uploads-storage` wraps its own `setItem`: an uncaught throw here escapes the effect
    // body and takes down the page with it.
    try {
      localStorage.setItem(PERSISTED_UPLOADS_KEY, JSON.stringify(next));
      // Snapshotted from OUR state, not from what was written: the question next time is whether our
      // copy has changed, and recording a row we deferred to a sibling on would read as a change.
      lastWrittenOwnRows.current = serialiseOwnRows<PersistedUpload>(persistedUploads);
    } catch (error) {
      // Left as it was, so the next write still sees our rows as changed and tries again.
      console.error('Error writing persisted uploads:', error);
    }
  }, [persistedUploads]);

  // Cross-component dismiss bridge: the requests dashboard / list render
  // UploadingStatusCell *outside* this provider, so they can't call
  // removePersistedUpload directly. They write to localStorage and dispatch
  // this event; we mirror the removal in our in-memory state so the next
  // progress-driven setPersistedUploads doesn't re-publish the dismissed entry.
  //
  // Must do the SAME cleanup as removePersistedUpload — this is the third door into "dismiss an
  // upload". It used to do only the localStorage mirror, so dismissing from the dashboard mid-resume
  // left the cancel flag unset and the file uploaded to completion. Keep all three in step.
  useEffect(() => {
    const handleRemoved = (e: Event) => {
      const detail = (e as CustomEvent<PersistedUploadRemovedDetail>).detail;
      const id = detail?.id;
      if (!id) return;
      // Defaults to 'dismissed' for the out-of-provider door, which predates the reason field.
      const reason = detail?.reason ?? 'dismissed';

      // Only a dismissal cancels. A completion is announced purely so siblings drop their mirror.
      if (reason === 'dismissed') {
        cancelledUploadIds.add(id);
        // Through the module registry, so a dismissal actually STOPS a transfer owned by another
        // provider. This used to read the per-instance registry and justify the miss with "the
        // sibling will see the cancel flag and stop of its own accord" — nothing in the tus callbacks
        // reads that flag, so the bytes ran on to completion and then announced themselves as a
        // completed upload, refreshing the gallery and prompting for metadata on a file the user had
        // just removed and whose blob was already deleted.
        const upload = findLiveUpload(id);
        if (upload) {
          upload.abort();
        }
        // Unconditionally, not only when this instance owns the tus object: a dismissal is global.
        unregisterUpload(id);
      }
      // Guarded so an instance that does not hold this id keeps its ARRAY IDENTITY. `filter` always
      // returns a new array, so the unguarded version re-rendered every mounted provider and re-ran
      // its localStorage write on every completion — see the merge in the write effect for what that
      // used to destroy. The merge makes this safe rather than load-bearing; it stays because
      // needless work in every provider on every completed upload is still needless.
      setUploadItems((prev) =>
        prev.some((u) => u.id === id) ? prev.filter((u) => u.id !== id) : prev,
      );
      setPersistedUploads((prev) =>
        prev.some((u) => u.id === id) ? prev.filter((u) => u.id !== id) : prev,
      );
    };
    window.addEventListener(PERSISTED_UPLOAD_REMOVED_EVENT, handleRemoved);
    return () => window.removeEventListener(PERSISTED_UPLOAD_REMOVED_EVENT, handleRemoved);
  }, []);

  const getUploadUrl = async (file: File, userPath: string[], overrideBrandId?: string) => {
    const effectiveBrandId = overrideBrandId || selectedBrand?.id;
    try {
      const response = await mediaService.getUploadUrl({
        file,
        userPath,
        brandId: effectiveBrandId,
      });
      console.debug(
        '🔧 MediaUploadContext.getUploadUrl: Passing brandId to mediaService:',
        effectiveBrandId,
      );
      return response;
    } catch (error: any) {
      Logger.error('❌ [MediaUploadContext] getUploadUrl failed', 'MediaUploadContext', {
        fileName: file.name,
        brandId: effectiveBrandId,
        errorMessage: error?.message || String(error),
        errorStatus: error?.response?.status,
        errorData: error?.response?.data
          ? JSON.stringify(error.response.data).substring(0, 500)
          : undefined,
      });
      return null;
    }
  };

  const getTimeoutStrategy = (fileSizeMB: number, baseTimeout: number) => {
    let dynamicTimeout: number;
    let maxRetries: number;
    let initialInterval: number;
    let maxInterval: number;
    let backoffMultiplier: number;

    if (fileSizeMB < 100) {
      // Small files: Quick processing
      dynamicTimeout = Math.min(baseTimeout + fileSizeMB * 1000, 90000); // +1s per MB, max 90s
      maxRetries = 6;
      initialInterval = 2000; // 2s
      maxInterval = 8000; // 8s
      backoffMultiplier = 1.5;
    } else if (fileSizeMB < 500) {
      // Medium-large files: Moderate patience
      dynamicTimeout = Math.min(baseTimeout + fileSizeMB * 2000, 180000); // +2s per MB, max 3min
      maxRetries = 8;
      initialInterval = 3000; // 3s
      maxInterval = 12000; // 12s
      backoffMultiplier = 1.4;
    } else if (fileSizeMB < 1000) {
      // Large files (500MB-1GB): High patience
      dynamicTimeout = Math.min(baseTimeout + fileSizeMB * 3000, 300000); // +3s per MB, max 5min
      maxRetries = 10;
      initialInterval = 5000; // 5s
      maxInterval = 20000; // 20s
      backoffMultiplier = 1.3;
    } else {
      // Very large files (1GB+): Maximum patience
      dynamicTimeout = Math.min(baseTimeout + fileSizeMB * 4000, 600000); // +4s per MB, max 10min
      maxRetries = 12;
      initialInterval = 8000; // 8s
      maxInterval = 30000; // 30s
      backoffMultiplier = 1.25;
    }

    return {
      dynamicTimeout,
      maxRetries,
      initialInterval,
      maxInterval,
      backoffMultiplier,
    };
  };

  const createUploadFromUploadItem = (
    item: UploadItem,
    resolve: (value: MediaObject) => void,
    reject: (reason?: any) => void,
    brandIdOverride?: string,
  ) => {
    const MAX_RETRIES = 5;

    // The `media` row to ask the server about. Differs from `item.id` only when a resume was
    // restarted onto a fresh record; `item.id` stays the client join key. Every OTHER use of an id
    // in this function is deliberately `item.id`.
    const serverMediaId = item.serverId ?? item.id;

    // Store file in IndexedDB if id is present and it's a file object
    if (item.id && item.file instanceof File) {
      storeFile(item.id, item.file).catch((err) => {
        console.error(`Failed to store file ${item.id} for resumable upload in IndexedDB:`, err);
      });
    } else {
      console.warn(
        `Skipped storing file in IndexedDB. ID: ${item.id}, File is File: ${item.file instanceof File}`,
      );
    }

    const upload = new tus.Upload(item.file, {
      uploadUrl: item.uploadUrl,
      headers: {
        'x-signature': item.uploadToken,
        Authorization: mediaService.headers.Authorization,
      },
      // Re-read the session token for EVERY request, rather than letting the one above stand for the
      // life of the transfer.
      //
      // `headers` is resolved once, when this `tus.Upload` is constructed, and tus then applies that
      // frozen string to every HEAD, PATCH and POST it sends. A multi-GB upload runs for longer than
      // a token lives — `getTimeoutStrategy` budgets up to ten minutes for the post-upload check
      // alone — so the token expires MID-TRANSFER and every subsequent chunk is sent with a dead
      // credential. tus owns its own XHR and never passes through `Http.ts`'s response interceptor,
      // which is the only thing in the app that refreshes on a 401 and retries, so nothing recovers
      // it: the upload simply fails, at whatever percentage it had reached.
      //
      // This is the root cause of the ambiguity that `isDeadUploadUrl` has to reason about — a 401
      // on the offset probe that means "our session", not "this URL is gone". Fixing it here does not
      // make that exclusion unnecessary (an expired token still cannot be refreshed from inside this
      // callback, only re-read), but it removes the far more common case: a session that was renewed
      // normally while a long upload was running.
      onBeforeRequest: (req) => {
        const authorization = mediaService.headers.Authorization;
        if (authorization) req.setHeader('Authorization', authorization);
      },
      retryDelays: [0, 1000, 3000, 5000, 10000],
      chunkSize: calculateOptimalChunkSize(item.file.size),
      addRequestId: true,
      // Off because nothing reads it any more. `findPreviousUploads()` was the only consumer and
      // both of its call sites are gone — resume now asks the server for a URL instead of trusting a
      // stored one. Left on, tus wrote a `tus::<fingerprint>` entry per upload that is dropped only
      // by `removeFingerprintOnSuccess`, so every failed or abandoned upload leaked one for good,
      // keyed by `${item.id}-…` so it could never be reused. That garbage shares the localStorage
      // budget with the two keys this feature actually depends on.
      storeFingerprintForResuming: false,
      removeFingerprintOnSuccess: true,
      fingerprint: async (file) => `${item.id}-${file.name}-${file.size}`,
      onShouldRetry(_error, retryAttempt) {
        return retryAttempt < MAX_RETRIES;
      },
      onError: (error) => {
        console.error('Upload failed:', item.fileName, error);
        // Mapped HERE rather than only at the resume call site, because a FRESH upload probes too:
        // `doUpload` constructs with `uploadUrl`, so tus sends the same offset HEAD and can be
        // refused with the same 401/403/408/409/423/429. Handling it only on the resume path left the
        // ordinary upload showing tus's "new upload cannot be created without an endpoint" — the
        // exact string this mapping exists to replace. The resume path layers its own dead-URL branch
        // on top of this and overwrites the message where it has something better to say.
        const refusal = describeRefusedProbe(readUploadFailureShape(error));
        const errorMessage =
          refusal ??
          (typeof error === 'string' ? error : (error as Error)?.message || 'Unknown upload error');
        setUploadItems((prevItems) =>
          prevItems.map((uploadItem) =>
            uploadItem.id === item.id
              ? { ...uploadItem, status: 'failed', progress: 0, error: errorMessage }
              : uploadItem,
          ),
        );
        // Release the registry entry. tus has stopped and this upload is dead, so leaving it
        // registered used to be merely untidy — a later resume just overwrote it. It is now
        // load-bearing: `startResumeAttempt` treats a registry entry as "already uploading", so a
        // stale one would refuse every Resume and Retry for this record for the rest of the session,
        // which is precisely the dead-end this PR exists to remove.
        if (item.id) {
          unregisterUpload(item.id);
        }
        reject(error);
      },
      onProgress: (bytesSent, bytesTotal) => {
        const progress = (bytesSent / bytesTotal) * 100;
        setUploadItems((prevItems) =>
          prevItems.map((uploadItem) =>
            uploadItem.id === item.id
              ? { ...uploadItem, progress, status: 'in-progress' }
              : uploadItem,
          ),
        );
        setPersistedUploads((prev) =>
          prev.map((upload) => (upload.id === item.id ? { ...upload, progress } : upload)),
        );
      },
      onSuccess: async () => {
        setUploadItems((prevItems) =>
          prevItems.map((uploadItem) =>
            uploadItem.id === item.id
              ? { ...uploadItem, status: 'uploaded', progress: 100 }
              : uploadItem,
          ),
        );

        // Push the completed mediaId into the background-upload store so the
        // request submission orchestrator can pick it up even after this
        // uploadItem gets removed from local state below.
        // The media id the request submission will attach — must be the SERVER row, not the client
        // join key, or the request attaches an id the backend deleted on a restarted resume.
        //
        // Guarded because it is the one call in this handler that runs FOREIGN code — a zustand
        // `set` notifies every subscriber synchronously, so any component's selector throwing takes
        // this callback down with it. That matters more than it reads: `unregisterUpload` is far
        // below, past the whole verification backoff, and there is no `finally` above it, so a throw
        // here leaks the module claim for this id permanently. `isResumeInFlight` would then be true
        // for the rest of the session and every Resume, Retry and auto-resume for the record would be
        // refused — the exact dead end this PR removes. The upload itself has already succeeded; a
        // broken subscriber must not be what strands it.
        if (serverMediaId) {
          try {
            useBackgroundUploadStore
              .getState()
              .recordCompletedUpload(item.fileName, item.fileSize, serverMediaId);
          } catch (error) {
            Logger.error(
              'Failed to record a completed upload in the background store',
              'MediaUploadContext',
              { id: item.id, serverMediaId, error },
            );
            // SWALLOWED ON PURPOSE, and deliberately not escalated to `markUploadFailed`. That was
            // tried and is wrong in the destructive direction: the persisted row and the IndexedDB
            // blob are removed moments later, so marking the upload failed turned a FINISHED upload
            // into one Retry could not resume — a worse outcome than whatever the throw caused.
            //
            // Swallowing is safe for a narrower reason than "the orchestrator has another path".
            // (It does have one, matching the uploadItem this callback flipped to 'uploaded' — but
            // that window can close between 2s polls on a small file, so `completedMediaIds` is
            // load-bearing, not a fallback.) The reason is that zustand applies the new state before
            // notifying subscribers: if the throw comes from a subscriber, the map is already
            // written, and the only thing lost is this callback's remaining work.
          }
        }

        let media: MediaObject | null = null;
        const startTime = Date.now();

        // Enhanced timeout calculation for very large files
        const fileSizeMB = item.file.size / (1024 * 1024);
        const baseTimeout = 30000; // 30 seconds base

        // Tiered timeout strategy for different file sizes
        const { dynamicTimeout, maxRetries, initialInterval, maxInterval, backoffMultiplier } =
          getTimeoutStrategy(fileSizeMB, baseTimeout);

        const verifyWithBackoff = async (
          attempt: number = 1,
          interval: number = initialInterval,
        ): Promise<MediaObject | null> => {
          const elapsed = Date.now() - startTime;

          // Check timeout - generous for large files
          if (elapsed >= dynamicTimeout) {
            return null;
          }

          // Check retry limit
          if (attempt > maxRetries) {
            return null;
          }

          try {
            // Longer individual request timeout for large files
            const requestTimeoutMs = fileSizeMB > 500 ? 20000 : 15000; // 20s for large files, 15s otherwise
            const controller = new AbortController();
            const requestTimeout = setTimeout(() => controller.abort(), requestTimeoutMs);

            const mediaResult = await Promise.race([
              mediaService.getMediaItemById(serverMediaId!, true, Date.now(), brandIdOverride),
              new Promise<null>((_, reject) => {
                controller.signal.addEventListener('abort', () =>
                  reject(new Error('Request timeout')),
                );
              }),
            ]).catch((error) => {
              clearTimeout(requestTimeout);
              if (error.message === 'Request timeout') {
                console.warn(
                  `⏱️ Individual request timeout (${requestTimeoutMs / 1000}s) on attempt ${attempt} for ${item.id}`,
                );
              } else {
                console.error(`❌ API error on attempt ${attempt} for ${item.id}:`, error.message);
              }
              return null;
            });

            clearTimeout(requestTimeout);

            if (mediaResult) {
              return mediaResult;
            }

            // Don't wait on the final attempt
            if (attempt >= maxRetries) {
              return null;
            }

            // Wait with current interval
            await wait(interval);

            // Calculate next interval with backoff
            const nextInterval = Math.min(interval * backoffMultiplier, maxInterval);

            // Recursive call with incremented attempt
            return verifyWithBackoff(attempt + 1, nextInterval);
          } catch (error) {
            console.error(`❌ Unexpected error on attempt ${attempt} for ${item.id}:`, error);

            // For unexpected errors, still retry but log the issue
            if (attempt >= maxRetries) {
              return null;
            }

            await wait(interval);

            const nextInterval = Math.min(interval * backoffMultiplier, maxInterval);
            return verifyWithBackoff(attempt + 1, nextInterval);
          }
        };

        // Start verification process
        media = await verifyWithBackoff();

        if (item.id) {
          try {
            await deleteFile(item.id); // Delete from IndexedDB on success
          } catch (err) {
            console.error(
              `Failed to delete file ${item.id} from IndexedDB after successful upload:`,
              err,
            );
          }
        }

        setUploadItems((prevItems) => prevItems.filter((uploadItem) => uploadItem.id !== item.id));
        unregisterUpload(item.id! as string);

        // BEFORE the `!media` branch, and that ordering is the fix. The cleanup used to sit below it,
        // so a post-upload verification TIMEOUT — tus succeeded, the backend just never confirmed the
        // row in time — returned early having already deleted the IndexedDB blob and the uploadItem
        // while leaving the persisted row in place. That row then looked resumable (no uploadItem, so
        // `validResumableUploads` surfaces it) but its blob was gone, so Resume failed
        // 'FILE_NOT_IN_CACHE' every time, forever, and re-uploading created a DUPLICATE media row
        // server-side — while `recordCompletedUpload` above had already published this id as complete.
        //
        // Removing it on the timeout path is right, not merely convenient: the bytes are on the server
        // and the local blob is gone, so there is nothing left here to resume with. The upload has
        // left this device either way.
        //
        // The fourth door out of the persisted list, and the one that was missed: success. Without
        // the broadcast a sibling provider keeps this record in its mirror and rewrites it back to
        // localStorage on its next unrelated state change, so a finished upload reappears under
        // "Resumable Uploads" — and since `deleteFile` above already dropped the blob, Resume on it
        // fails "File not found in local cache" forever. 'completed', not 'dismissed': see
        // PersistedUploadRemovedReason for why the cancel flag must not be set here.
        setPersistedUploads((prev) => prev.filter((upload) => upload.id !== item.id));
        if (item.id) {
          // `media` rides along so listeners can offer the metadata prompt for an upload they did not
          // start; it is null on the timeout path below, where there is no row to describe yet.
          announcePersistedUploadRemoved(item.id, 'completed', {
            media,
            path: item.path ?? [],
          });
        }

        if (!media) {
          const timeoutMessage = `Media item ${item.id} not found after ${Math.round(dynamicTimeout / 1000)} seconds timeout (${fileSizeMB.toFixed(1)}MB file)`;
          Logger.warn(timeoutMessage);
          reject(new Error(timeoutMessage));
          return;
        }
        resolve(media);
      },
    });
    return upload;
  };

  const doUpload = (item: UploadItem, brandIdOverride?: string) => {
    return new Promise<MediaObject>((resolve, reject) => {
      if (!item.id) {
        // This case should ideally not happen if handleUpload correctly assigns IDs
        console.error('Item ID is missing before starting upload:', item);
        reject(new Error('Item ID is missing.'));
        return;
      }
      // A genuinely new upload, so a cancel flag left from a previous record under this id is stale.
      // The only place the flag is cleared: unlike the resume entries this is never reached by an
      // automatic trigger, so it cannot erase a Remove the user just made.
      cancelledUploadIds.delete(item.id);

      // Save to persisted uploads (for our localStorage based persistence).
      //
      // Filter-then-append rather than `uniqBy([...prev, row], 'id')`, which keeps the FIRST
      // occurrence — so an existing row for this id survived and the fresh URL/token/progress were
      // the ones discarded.
      setPersistedUploads((prev) =>
        uniqBy(
          [
            ...prev.filter((u) => u.id !== item.id),
            {
              id: item.id!,
              fileName: item.fileName,
              fileSize: item.fileSize,
              progress: item.progress,
              uploadUrl: item.uploadUrl,
              uploadToken: item.uploadToken,
              path: item.path,
              fileType: item.file.type,
              // Captured at start so a resume can re-request under the SAME brand rather than
              // whichever one is active later.
              brandId: brandIdOverride || selectedBrand?.id,
            },
          ],
          'id',
        ),
      );

      try {
        // INSIDE the try, matching `runResumeAttempt`. This runs `storeFile`, reads `item.file.size`
        // and constructs the `tus.Upload` (which validates its options) — all of which can throw, and
        // all of which used to escape past the compensation below, leaving the row stuck at 'pending'
        // exactly as an uncaught `start()` did.
        //
        // createUploadFromUploadItem now handles storing the file in IndexedDB
        const upload = createUploadFromUploadItem(item, resolve, reject, brandIdOverride);
        registerUpload(item.id! as string, upload);
        // Wrapped for the same reason as the resume path's `upload.start()`: this is synchronous code
        // that can throw — `start()` runs the first request setup — and a throw between the register
        // and tus taking over leaves the module claim with nothing to release it, since neither
        // callback will ever fire. That claim then refuses every later Resume and Retry for the
        // record for the rest of the session, which is precisely the dead end this PR removes. It was
        // survivable only while the unmount effect swept the registry; nothing sweeps it now, and
        // nothing should — the sweep is what let a second transfer start (see `activeUploadIds`).
        upload.start();
      } catch (error) {
        unregisterUpload(item.id! as string);
        // Mark the item FAILED as well as releasing the claim, matching `runResumeAttempt`'s catch.
        // Releasing alone left the row stuck at 'pending' for the rest of the session: excluded from
        // `validResumableUploads` (which wants no item, or 'failed'), so not offered for Resume, and
        // rejected by `handleUpload` as a duplicate of itself, so not re-uploadable either — with its
        // IndexedDB blob never collected.
        const message =
          typeof error === 'string' ? error : (error as Error)?.message || 'Upload failed to start';
        setUploadItems((prev) =>
          prev.map((u) => (u.id === item.id ? { ...u, status: 'failed', error: message } : u)),
        );
        // `reject`, not `throw`: matching the guard 50 lines above, and because a throw out of the
        // executor is silently swallowed if tus has already settled this promise via its own error
        // path. `reject` on an already-settled promise is a no-op either way.
        reject(error);
      }
    });
  };

  const handleUpload = async (params: {
    files: File[];
    userPath: string[];
    onSuccess: (items: UploadItem[]) => void;
    onError: (error: any) => void;
    onSingleFileError: (item: UploadItem, error: any) => void;
    onSingleFileSuccess: (item: UploadItem, mediaObject: MediaObject) => void;
    brandId?: string; // Optional brandId override for WebView uploads
  }) => {
    const { files, userPath, onSuccess, onError, onSingleFileError, onSingleFileSuccess, brandId } =
      params;

    const storageUsage = await mediaService.getStorageUsageStatus();

    if (!storageUsage) {
      Logger.error('❌ [MediaUploadContext] Storage usage not available', 'MediaUploadContext');
      onError(new Error('Storage usage not available'));
      return;
    }

    // Check for duplicate filenames in current upload queue
    const duplicateFiles: string[] = [];
    files.forEach((file) => {
      const isDuplicate = uploadItems.some(
        (existingItem) => existingItem.fileName === file.name && existingItem.status !== 'failed', // Don't consider failed uploads as duplicates
      );
      if (isDuplicate) {
        duplicateFiles.push(file.name);
      }
    });

    // If any duplicates found, show user-friendly error message
    if (duplicateFiles.length > 0) {
      const duplicateList =
        duplicateFiles.length === 1
          ? `'${duplicateFiles[0]}'`
          : duplicateFiles.map((name) => `'${name}'`).join(', ');

      const errorMessage =
        duplicateFiles.length === 1
          ? `A file named ${duplicateList} has already been uploaded.`
          : `Files with these names have already been uploaded: ${duplicateList}.`;

      onError(new Error(errorMessage));
      return;
    }

    // Check individual file size limit
    const oversizedFiles = files.filter((file) => file.size > MAX_SINGLE_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((f) => f.name).join(', ');
      onError(
        new Error(
          `Single file uploads are limited to 10 GB. The following file(s) exceed this limit: ${fileNames}. For larger files, use an external link option below or contact support for custom workflows.`,
        ),
      );
      return;
    }

    const availableSpace = storageUsage.max_storage - storageUsage.used_storage;
    const totalUploadSize = files.reduce((total, file) => total + file.size, 0);
    if (totalUploadSize > availableSpace) {
      Logger.info('❌ [MediaUploadContext] Not enough storage space', 'MediaUploadContext');
    }

    const newUploadItems = files.map<UploadItem>((file) => ({
      id: undefined, // ID will be set after getting upload URL
      tempId: uuidv4(),
      fileName: file.name,
      fileSize: file.size,
      file,
      progress: 0,
      status: 'pending',
      uploadUrl: '',
      uploadToken: '',
      path: userPath,
    }));
    setUploadItems((prev) => [...prev, ...newUploadItems]);
    if (files.length > 0) setIsUploading(true);

    const itemsWithUrlPromises = newUploadItems.map(async (item) => {
      const urlDetails = await getUploadUrl(item.file, item.path, brandId);
      if (!urlDetails || !urlDetails.id || !urlDetails.upload_url || !urlDetails.upload_token) {
        Logger.error('❌ [MediaUploadContext] Failed to get upload URL', 'MediaUploadContext', {
          fileName: item.fileName,
          urlDetails,
        });
        toast({
          title: 'Failed to prepare upload',
          description: `Could not get upload details for: ${item.fileName}`,
          variant: 'destructive',
        });
        onSingleFileError(item, new Error('Failed to get upload URL/ID'));
        return null; // Indicate failure for this item
      }
      return {
        ...item,
        id: urlDetails.id,
        uploadUrl: urlDetails.upload_url,
        uploadToken: urlDetails.upload_token,
        status: 'pending' as const, // Explicitly set status
      };
    });

    const processedItems = (await Promise.all(itemsWithUrlPromises)).filter(
      Boolean,
    ) as UploadItem[];

    // Update uploadItems state with new IDs and URLs
    setUploadItems((prev) =>
      prev
        .map((ui) => {
          const processed = processedItems.find((p) => p.tempId === ui.tempId);
          return processed ? { ...ui, ...processed, status: 'pending' as const } : ui;
        })
        .filter((ui) => ui.status !== 'failed'),
    ); // Remove items that failed to get URL

    if (!processedItems.length && files.length > 0) {
      Logger.error('❌ [MediaUploadContext] No files prepared for upload', 'MediaUploadContext');
      onError(new Error('Failed to prepare any files for upload.'));
      setIsUploading(false);
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 100)); // Allow state to settle

    // Only the files whose bytes actually landed. onSuccess used to receive
    // every item that had merely been *issued* an upload URL, and because it
    // runs after the per-file handlers it overwrote their error state: consumers
    // marked failed files 'completed', selected them for submission, or removed
    // their rows outright, so an upload that failed looked like one that worked.
    const succeededItems: UploadItem[] = [];

    const uploadPromises = processedItems.map((item) =>
      doUpload(item, brandId)
        .then((mediaObject) => {
          succeededItems.push(item);
          onSingleFileSuccess(item, mediaObject);
        })
        .catch((error) => {
          Logger.error('❌ [MediaUploadContext] doUpload failed', 'MediaUploadContext', {
            fileName: item.fileName,
            error: error?.message || error,
          });
          onSingleFileError(item, error);
        }),
    );

    Promise.all(uploadPromises)
      .then(() => {
        // Every file has been handled one way or the other; this reports the
        // ones that succeeded, which may be none.
        onSuccess(succeededItems);
      })
      .catch((error) => {
        Logger.error('❌ [MediaUploadContext] Promise.all failed', 'MediaUploadContext', { error });
        // This catch is for errors in Promise.all setup, individual errors handled above
        onError(error);
      })
      .finally(() => {
        // Check if any uploads are still 'in-progress' or 'pending'
        const stillUploading = uploadItems.some(
          (item) => item.status === 'in-progress' || item.status === 'pending',
        );
        if (!stillUploading) {
          setIsUploading(false);
        }
      });
  };

  /**
   * Is there something to cancel for this record?
   *
   * The registry entry alone, deliberately. This used to also require a matching `uploadItems` row
   * in status 'in-progress' — the same lookup `stopUpload` used to gate its abort on — so on a miss
   * (a stale render closure, or `resumeUpload` having just filtered the row out ahead of its
   * IndexedDB read) the Cancel button went disabled at exactly the moment there was a live tus
   * upload to stop. Fixing `stopUpload` alone would have been unreachable: this predicate gates the
   * button that calls it (media-library-page:3080, media-source-field:297).
   *
   * A registered entry means tus is running for this record, which is the whole precondition for
   * cancelling. Dropping the 'in-progress' clause also makes the button live during the brief
   * 'pending' window after `upload.start()` and before the first progress event, which was
   * previously an uncancellable gap.
   *
   * But NOT once the item reads 'uploaded'. The registry entry outlives the transfer: `onSuccess`
   * flips the status to 'uploaded' and only deletes the entry after `verifyWithBackoff`, which
   * polls for minutes on a large file. Cancelling in that window succeeds at the UI level — toast,
   * rows removed, blob deleted — while the verification chain runs on untouched and adds the file
   * to the gallery moments after the user was told it was cancelled. A missing item row still reads
   * as cancellable, which is the stale-lookup case above.
   */
  const uploadCanBeCancelled = (id: string) => {
    // THIS instance's registry, deliberately, even though `liveUploads` now knows about siblings.
    // Both call sites only reach this predicate from inside their own `uploadItems` row
    // (media-library-page renders the button inside `uploadItems.map`; media-source-field gates on
    // `findUploadItemByFileName`), so a module-wide check buys nothing they can reach — and in the one
    // shape where the two registries differ, an id live in a SIBLING with no row here, the missing row
    // makes the 'uploaded' clause below vacuous (`undefined !== 'uploaded'`) and would reopen the
    // post-success verification window that clause exists to close.
    //
    // The consequence is that a transfer whose provider unmounted has no Cancel button on remount,
    // because no row is rendered for it at all. `stopUpload` CAN now stop it (it goes through
    // `liveUploads`); what is missing is a surface that offers the record. That is a UI gap, not a
    // lock bug, and it is left for a follow-up rather than widened into here.
    if (!currentUploads.current[id]) return false;
    return uploadItems.find((item) => item.id === id)?.status !== 'uploaded';
  };

  const stopUpload = async (id: string) => {
    // Recorded before anything else so an in-flight resume awaiting getUploadUrl sees it.
    cancelledUploadIds.add(id);
    // Module registry: a Cancel has to stop the transfer whichever instance owns it, including one
    // whose provider has since unmounted. See `liveUploads`.
    const upload = findLiveUpload(id);

    // Gated on `upload` alone, matching removePersistedUpload and the removed-event handler. It also
    // required a matching `uploadItems` row, which is not always present at click time — a stale
    // render closure, or `resumeUpload` having just filtered the row out ahead of its IndexedDB read.
    // On a miss this set the cancel flag and deleted the row and blob but never aborted: the file
    // uploaded to completion despite Cancel (the flag gates nothing inside the tus callbacks), and
    // the orphaned registry entry made `acquireResumeLock` refuse every later Resume and Retry for
    // this record for the rest of the session.
    if (upload) {
      upload.abort();
    }
    // Outside the `if`, for the same reason as the removed-event handler: a cancel is global, so the
    // module claim must clear even when the live transfer belongs to a sibling instance.
    unregisterUpload(id);

    // Call the cancel API endpoint to notify the server
    // Commented out as it is no longer used
    // if (id) {
    //   try {
    //     await Api.media.cancelUpload(id);
    //     console.log(`Successfully cancelled upload ${id} on server`);
    //   } catch (error) {
    //     console.error(`Failed to cancel upload ${id} on server:`, error);
    //     // Continue with local cleanup even if API call fails
    //     toast({
    //       title: 'Warning',
    //       description: 'Upload cancelled locally, but server notification failed.',
    //       variant: 'destructive',
    //     });
    //   }
    // }

    setUploadItems((prev) => prev.filter((i) => i.id !== id));
    setPersistedUploads((prev) => prev.filter((i) => i.id !== id));
    announcePersistedUploadRemoved(id);
    if (id) {
      try {
        await deleteFile(id);
      } catch (err) {
        console.error(`Failed to delete file ${id} from IndexedDB on removal:`, err);
      }
    }
  };

  const removePersistedUpload = async (id: string) => {
    cancelledUploadIds.add(id);

    // Drop the uploadItems row too, not just the persisted one. On "Resume, then Remove while
    // getUploadUrl is in flight" the resume has already published an optimistic 'pending' row;
    // leaving it stranded a row with no persisted record behind it — invisible to
    // validResumableUploads and with no control left to dismiss it. Matches stopUpload.
    //
    // Module registry for the abort itself, for the same reason as stopUpload: a Remove must stop a
    // transfer owned by another instance, not just tidy the UI around it. See `liveUploads`.
    const upload = findLiveUpload(id);
    if (upload) {
      upload.abort();
    }
    unregisterUpload(id);
    setUploadItems((prev) => prev.filter((item) => item.id !== id));
    setPersistedUploads((prev) => prev.filter((item) => item.id !== id));
    announcePersistedUploadRemoved(id);
    if (id) {
      try {
        await deleteFile(id);
      } catch (err) {
        console.error(
          `Failed to delete file ${id} from IndexedDB on removal of persisted upload:`,
          err,
        );
      }
    }
  };

  const validResumableUploads = () => {
    return uniqBy(
      persistedUploads.filter((item) => {
        const uploadItem = uploadItems.find((upload) => upload.id === item.id);
        return !uploadItem || uploadItem.status === 'failed';
      }),
      'id',
    );
  };

  /**
   * Recover from a dead upload URL by deleting the record that keeps producing it.
   *
   * THE HALF OF THE BUG THAT ASKING THE SERVER DOES NOT FIX. `uploadMediaV2` replays a still-
   * `pending` record's stored `upload_url` VERBATIM inside UPLOAD_VALIDITY_MINUTES (120) — it does
   * not re-sign it (SupabaseMediaService.ts:345-364). So once that stored URL has expired, every
   * Resume was handed the same dead string back and reproduced the identical HEAD 400. The only
   * thing that ever cleared it was MediaUploadMonitor flipping the row to `failed` at
   * MEDIA_UPLOAD_TIMEOUT_SECONDS (3600s) — an hour, far outside the window in which anyone retries.
   *
   * Deleting the pending row removes the thing being replayed, so the next request falls through to
   * the mint branch and gets a genuinely fresh URL. `cancelUpload` is exactly that delete: it
   * DELETEs the tus resource and then the row, and refuses anything not still `pending`.
   *
   * Bounded on purpose — one cancel, no automatic re-request. The user's next Resume is the retry,
   * and it now succeeds instead of looping. Best-effort: a failed cancel leaves precisely the old
   * behaviour, so this can only improve on it, and it must never mask the error being reported.
   */
  const discardDeadUploadRecord = async (serverId: string, clientId?: string) => {
    Logger.warn(
      '[MediaUploadContext] Upload URL is dead; discarding the pending record so the next Resume mints a fresh one',
      'MediaUploadContext',
      { id: clientId, serverId },
    );
    try {
      await Api.media.cancelUpload(serverId);
    } catch (error) {
      // Logged, not returned on. A HEAD 404 means the server has already lost the upload, so
      // `cancelUpload` refusing (it only accepts a still-`pending` row) is the LIKELIEST outcome on
      // the branch where the record is most certainly dead — and that was exactly the branch that
      // used to skip the local cleanup below.
      Logger.warn(
        '[MediaUploadContext] Could not discard the dead upload record; the next Resume may repeat this failure',
        'MediaUploadContext',
        { id: clientId, serverId, error },
      );
    }
    if (!clientId) return;
    // Clear what we now know is dead. The next resume overwrites all of it from the server's answer
    // anyway, so this is belt-and-braces — but a persisted URL that is known-dead is a trap for any
    // reader that trusts it, and `progress` must not survive: those bytes are gone with the record.
    //
    // Through `applyResumePersistence`, like the resume path, rather than an inline `prev.map`. The
    // two were behaviourally identical, but a second copy of "patch one row by id" is a second place
    // for the never-create and never-re-key contracts documented on that helper to drift away from.
    setPersistedUploads((prev) =>
      applyResumePersistence<PersistedUpload>(prev, clientId, {
        uploadUrl: '',
        uploadToken: '',
        serverId: undefined,
        progress: 0,
      }),
    );
  };

  /**
   * Would `acquireResumeLock` refuse right now? Same test, no side effect.
   *
   * For callers that spend something irreversible before resuming — `use-retry-failed-upload`
   * increments `userRetryCount` toward MAX_USER_RETRIES — so they can bail before paying rather than
   * discovering the refusal afterwards with nothing to refund. There is no window between this and
   * the acquire for such a caller: everything from here to `resumeUpload`'s synchronous
   * `acquireResumeLock` runs without an await.
   */
  const isResumeInFlight = (id: string): boolean =>
    resumingUploadIds.has(id) || activeUploadIds.has(id);

  /**
   * Claim the sole right to resume `id`, or refuse. Returns a release function, or null.
   *
   * Three independent triggers exist for one record — the orchestrator's mount effect, the
   * media-library Resume button, and the dashboard retry cell. Two overlapping produced two
   * `getUploadUrl` calls and two `tus.Upload`s, the second orphaning the first: two PATCH streams
   * for one file and a Cancel that stopped only the newer one.
   *
   * Refusing is silent. A refusal means another attempt owns the record and will drive it to success
   * or to a real failure, so nothing needs resolving here. Reporting through `onError` was worse:
   * both automatic callers treat it as terminal, so a duplicate trigger flipped a healthy in-flight
   * upload to 'failed', and the orchestrator's completion branches are gated on 'uploading'.
   *
   * `activeUploadIds` is consulted as well as the flag set, because the flag is released once
   * `upload.start()` is called while the transfer then runs for minutes.
   *
   * BOTH sets are module-level, which is what makes this hold with several providers mounted at once.
   * The already-uploading arm used to read `currentUploads`, a per-instance ref, so it only ever
   * spoke for the asking instance — see `activeUploadIds` for the duplicate transfer that let
   * through.
   */
  const acquireResumeLock = (id: string): (() => void) | null => {
    if (isResumeInFlight(id)) {
      Logger.info(
        '[MediaUploadContext] Resume refused — another attempt already owns this record',
        'MediaUploadContext',
        { id },
      );
      return null;
    }
    resumingUploadIds.add(id);
    let released = false;
    // Idempotent, so a caller that hands its lock to startResumeAttempt can still release in its own
    // `finally` without freeing a lock a later attempt has since taken.
    return () => {
      if (released) return;
      released = true;
      resumingUploadIds.delete(id);
    };
  };

  /**
   * Run one resume attempt under the lock. Pass `heldLock` when the caller already holds it.
   *
   * Does not clear the cancel flag: the orchestrator and retry cell come through here, so clearing
   * would let an automatic trigger erase a Remove the user just performed. A genuinely new upload
   * clears its own stale flag in `doUpload`.
   */
  const startResumeAttempt = async (
    params: {
      file: File;
      persistedUpload: PersistedUpload;
      onSuccess: (item: UploadItem, mediaObject: MediaObject) => void;
      onError: (error: any) => void;
    },
    heldLock?: () => void,
  ): Promise<ResumeOutcome> => {
    const release = heldLock ?? acquireResumeLock(params.persistedUpload.id);
    // Still silent for the callbacks — see ResumeOutcome for why a refusal must not reach onError —
    // but no longer invisible: the caller gets 'refused' and can release whatever it allocated for
    // this click.
    if (!release) return 'refused';
    try {
      await runResumeAttempt(params);
      return 'attempted';
    } finally {
      // From here the record is either registered in `currentUploads` — which acquireResumeLock
      // consults — or it failed and is legitimately retryable.
      release();
    }
  };

  const runResumeAttempt = async (params: {
    file: File;
    persistedUpload: PersistedUpload;
    onSuccess: (item: UploadItem, mediaObject: MediaObject) => void;
    onError: (error: any) => void;
  }) => {
    const { file, persistedUpload, onSuccess, onError } = params;

    const newFileSignature = `${file.name}-${file.size}`;
    const prevFileSignature = `${persistedUpload.fileName}-${persistedUpload.fileSize}`;
    if (newFileSignature !== prevFileSignature) {
      onError(new Error('File signature does not match. Please select the correct file.'));
      return;
    }

    const uploadItem: UploadItem = {
      id: persistedUpload.id,
      tempId: uuidv4(),
      fileName: file.name,
      fileSize: file.size,
      file: file,
      // Normalised for the same reason as `path` below: the field is typed `number`, but a row
      // rehydrated from JSON where an undefined was dropped carries none, and TS cannot catch it.
      // An undefined here reached `resumeStartProgress` and then the orchestrator's
      // `Math.abs(a - b) >= 2` progress-change test as NaN, which is always false — so the request's
      // progress bar never advanced again for that file.
      progress: persistedUpload.progress ?? 0,
      status: 'pending', // Will change to in-progress once tus starts
      uploadUrl: persistedUpload.uploadUrl,
      uploadToken: persistedUpload.uploadToken,
      // Normalised, because the field is typed `string[]` but a record persisted before `path`
      // existed — or rehydrated from JSON where an undefined was dropped — has none. The next line
      // down used to defend against that with `?? []` while the item carried the undefined onward
      // into state, so any reader doing `path.length` on it would throw.
      path: persistedUpload.path ?? [],
      error: undefined,
    };

    // Replace an existing uploadItem with the same id (e.g. a prior failed
    // attempt being retried in-session), otherwise append. Previously this
    // used uniqBy which kept the first occurrence and silently dropped the
    // fresh retry item.
    setUploadItems((prev) => {
      const hasExisting = prev.some((u) => u.id === uploadItem.id);
      return hasExisting
        ? prev.map((u) => (u.id === uploadItem.id ? uploadItem : u))
        : [...prev, uploadItem];
    });
    setIsUploading(true);

    // Ask the server for an upload URL rather than reusing the one persisted on the device — the fix
    // for "unable to resume upload (new upload cannot be created without an endpoint)". One request
    // covers both cases: uploadMediaV2 replays a still-valid pending record's URL so tus learns the
    // offset and continues, or past the window mints a new path with a new id for a clean restart.
    //
    // The client id is fixed for this whole function; the server's answer no longer re-keys the item.
    const itemId = uploadItem.id;

    const markResumeFailed = (error: unknown) => {
      // The item was published as 'pending' above. An early return that only called onError left it
      // there forever: it vanished from validResumableUploads, the orchestrator never marked the
      // request failed, and handleUpload refused the same file as a duplicate.
      //
      // tus reports some failures as a bare string, so reading only `.message` replaced the real
      // text with a generic 'Resume failed'.
      const message =
        typeof error === 'string' ? error : (error as Error)?.message || 'Resume failed';
      setUploadItems((prev) =>
        prev.map((u) => (u.id === itemId ? { ...u, status: 'failed', error: message } : u)),
      );
      onError(error);
    };

    // Scoped to the brand the upload belonged to, falling back to the active brand for records
    // persisted before brandId was captured. The backend's pending-record lookup keys on user_id only
    // when it is present — the clause is conditional — so sending the right brand is what keeps this
    // scoped; it is not enforced for us.
    const resumeBrand = resumeBrandScope(persistedUpload.brandId, selectedBrand?.id);

    // Wait out a discard of this record that a previous attempt started, BEFORE asking for a URL.
    // The backend answers a still-`pending` match with that record's own `upload_url`, so requesting
    // one while the cancel is in flight hands back the dead URL we are here to replace — the loop this
    // PR exists to break, reachable by nothing more than pressing Resume twice quickly.
    //
    // Placed at the single choke point every entry funnels through (the media-library button, the
    // retry hook that reads localStorage directly, and the orchestrator's mount-time resume), and
    // above the cancelled-while-awaiting check below so a Remove landing during the wait is still
    // honoured by it. Bounded and non-fatal by contract: a hung or failed discard resolves the wait
    // and we proceed exactly as before the fix.
    await deadRecordDiscardGate.wait(persistedUpload.id);

    const slip = await getUploadUrl(file, persistedUpload.path ?? [], resumeBrand);

    // Cancelled while we were awaiting? Then stop — stopUpload could not abort us, because nothing
    // was registered yet, and starting now would upload a file the user removed.
    if (cancelledUploadIds.has(persistedUpload.id)) {
      Logger.info(
        '[MediaUploadContext] Resume abandoned — cancelled while requesting an upload URL',
        'MediaUploadContext',
        { id: persistedUpload.id },
      );
      // Retract the optimistic 'pending' row published at entry, or it stays as a ghost in "Currently
      // uploading" that can never be dismissed — nothing is registered in `currentUploads` yet, so
      // `uploadCanBeCancelled` is false and the cancel button is disabled for good.
      setUploadItems((prev) => prev.filter((u) => u.id !== itemId));
      // Give back the record the server just minted for an upload that will now never happen. Without
      // this, every Resume-then-Remove left a `pending` media row — cleared only when
      // MediaUploadMonitor times it out at 3600s — and a partial storage object that is never
      // collected at all. Best-effort and unawaited: the local retraction above is what the user is
      // waiting on, and a failed cancel leaves exactly the old behaviour.
      if (slip?.id) {
        void Api.media.cancelUpload(slip.id).catch((error) => {
          Logger.warn(
            '[MediaUploadContext] Could not release the upload record for an abandoned resume',
            'MediaUploadContext',
            { id: persistedUpload.id, serverId: slip.id, error },
          );
        });
      }
      // Reported, matching the cancelled-during-getFile path: the retry hook has already spent a slot
      // and flipped the row to 'uploading', and nothing else would resolve it.
      onError(new Error('Upload was removed while preparing to resume.'));
      return;
    }

    // Same guard the normal upload path uses (`!urlDetails.id || !upload_url || !upload_token`).
    // `id` matters as much as the URL — see below.
    if (!slip?.upload_url || !slip?.upload_token || !slip?.id) {
      markResumeFailed(new Error('Could not get an upload URL to resume with. Please try again.'));
      return;
    }

    // CARRY the server's id, do not become it. Past the pending-record window the backend deletes
    // the old media row and inserts a new one, so `persistedUpload.id` names a row that no longer
    // exists and every SERVER lookup has to use `slip.id` — verification previously polled the
    // deleted row, 404'd for the whole backoff window and reported failure after a full re-upload.
    //
    // But the client id must NOT change with it. Re-keying detached the item and the persisted row
    // from every id-keyed reader outside this provider, since `PendingUpload.tempId` is never
    // remapped. `id` joins client state, `serverId` addresses the server.
    const attemptItem: UploadItem = {
      ...uploadItem,
      serverId: slip.id,
      uploadUrl: slip.upload_url,
      uploadToken: slip.upload_token,
      // Zero when the server minted a new record: those bytes no longer exist server-side, so showing
      // the old percentage makes progress tick backwards on the first real update. Compared against
      // the server id we last held — comparing against the client id reads every later
      // continue-from-offset as a fresh restart and resets a genuine resume to 0.
      // `uploadItem.progress`, not `persistedUpload.progress` — the normalised value, or this
      // bypasses that normalisation on the exact path it exists for. `resumeStartProgress` returns
      // its third argument verbatim on the continue branch, so a legacy row with no `progress` put
      // an undefined straight back into the item and the persisted patch, and the orchestrator's
      // `Math.abs(a - b) >= 2` change test read NaN and never advanced the bar again. The restart
      // branch returned 0 and hid it, so only working resumes were affected.
      progress: resumeStartProgress(
        persistedUpload.serverId ?? persistedUpload.id,
        slip.id,
        uploadItem.progress,
      ),
    };

    // Upsert, not map. A `.map` silently does nothing if the optimistic row was dropped while we
    // awaited, leaving the upload running with no row at all: no progress, a dead cancel button, and
    // Resume offered for a file already uploading.
    setUploadItems((prev) => {
      const hasExisting = prev.some((u) => u.id === itemId);
      return hasExisting
        ? prev.map((u) => (u.id === itemId ? attemptItem : u))
        : [...prev, attemptItem];
    });

    // Update the persisted row in place; never delete-and-reinsert. It keeps its id and is never
    // absent for a moment — the old code deleted it on the restart branch and created nothing, so a
    // later mid-transfer failure had no record and could be neither resumed nor retried.
    const resumePatch = {
      serverId: slip.id,
      uploadUrl: slip.upload_url,
      uploadToken: slip.upload_token,
      progress: attemptItem.progress,
      // Only re-stamped when the row already named a brand — never when `resumeBrand` came from
      // the active-brand fallback. Persisting the fallback made a guess permanent: a row started
      // under brand A before brandId was captured, resumed while brand B was selected, was stamped
      // B for good, so every later resume re-requested under B too and the media landed on the
      // wrong brand with no way to correct it. The fallback still scopes THIS request; it just
      // does not get written back as if it were known.
      ...(persistedUpload.brandId ? { brandId: persistedUpload.brandId } : {}),
    };

    setPersistedUploads((prev) => {
      const next = applyResumePersistence(prev, persistedUpload.id, resumePatch);
      if (prev.some((u) => u.id === persistedUpload.id)) return next;

      // The row is not in THIS provider's mirror. Two very different causes, and
      // `applyResumePersistence` cannot tell them apart — by contract it only ever updates, so both
      // came out as "silently drop the server's answer".
      //
      //  - Dismissed mid-resume. Must stay dismissed; re-adding it is the resurrection bug that
      //    property 2 of applyResumePersistence exists to prevent. The cancel flag is module-level,
      //    so it is set no matter which provider took the dismissal, and it is read here off a ref
      //    inside the updater so it reflects a Remove that landed after the check above.
      //  - This mirror simply never had it. Routine: the provider is mounted in six places, and
      //    use-retry-failed-upload reads the row straight from localStorage precisely "so the hook
      //    works even if the provider's React state is one render behind (or absent)". Dropping the
      //    patch here left the stale URL and no serverId persisted, so the next failure resumed from
      //    the dead URL again and `resumeStartProgress` compared against the wrong id and reset a
      //    genuine continue-from-offset to 0. Worse, this provider then rewrote its own array to
      //    localStorage and deleted the row outright.
      if (cancelledUploadIds.has(persistedUpload.id)) return next;
      return [...next, { ...persistedUpload, ...resumePatch }];
    });

    // No blob migration needed: the IndexedDB key is the client id, which has not changed.
    //
    // Still no retry-inside-onError: recovery is one bounded step, and the NEXT explicit Resume
    // performs it. What follows is a cancel, and it is the reason `isDeadUploadUrl` is structural.
    // The old escalation called cancelUpload on ANY refused probe, so a transient 503 destroyed
    // every uploaded byte. Gated on a HEAD 4xx minus the six codes `isDeadUploadUrl` excludes — 401
    // and 403 (our session, not the URL), 409 and 423 (tus's own retryable pair), 408 and 429 ("ask
    // again") — it is safe: what is left means the server will not serve this upload's offset, so
    // there are no reachable bytes to protect.
    // Wrapped, because everything from here on is synchronous code that CAN throw — `new tus.Upload`
    // validates its options, and `upload.start()` runs the first request setup — while the item has
    // already been published as 'pending'. `markResumeFailed` is only reached from the two explicit
    // branches above it, and neither `startResumeAttempt` nor `resumeUpload` marks the item on a
    // throw, so an exception here stranded the row at 'pending' for the rest of the session: excluded
    // from `validResumableUploads` (which wants no item, or 'failed'), and rejected by `handleUpload`
    // as a duplicate of itself.
    try {
      const upload = createUploadFromUploadItem(
        attemptItem,
        (mediaObject) => onSuccess(attemptItem, mediaObject),
        (error) => {
          const failure = readUploadFailureShape(error);
          if (isDeadUploadUrl(failure)) {
            // Through the gate, not fired and forgotten. The message below tells the user to press
            // Resume again, and until this cancel lands the backend still matches the pending row and
            // answers with the SAME dead URL — so the next attempt waits on this. Not awaited here:
            // the row is already 'failed' and clickable, so awaiting before the message would narrow
            // the window without closing it; the wait belongs on the resume side. `run` never rejects.
            void deadRecordDiscardGate.run(itemId, () => discardDeadUploadRecord(slip.id, itemId));
            // Replaces tus's "new upload cannot be created without an endpoint", which named an
            // internal precondition and told the user nothing about what to do. The recovery above
            // makes the next press the one that works, so say so.
            markResumeFailed(
              new Error('This upload link had expired. Press Resume again to start it over.'),
            );
            return;
          }
          // A probe refusal we deliberately do NOT discard on — an expired session, a brief lock, a
          // rate limit. Nothing is deleted, but the user still gets told what happened and what to do
          // instead of tus's "new upload cannot be created without an endpoint".
          const refusal = describeRefusedProbe(failure);
          if (refusal) {
            markResumeFailed(new Error(refusal));
            return;
          }
          markResumeFailed(error);
        },
        // Same brand as the URL request above — createUploadFromUploadItem uses it for the
        // post-upload media lookup, which would otherwise query the active brand.
        resumeBrand,
      );

      registerUpload(itemId!, upload);
      // Do not resumeFromPreviousUpload — it would overwrite the server's answer with the stale
      // stored URL, which is the bug. tus probes `uploadUrl` on start and continues from the
      // reported offset by itself, so continuing from the previous offset needs no extra call.
      upload.start();
    } catch (error) {
      // Release the claim first: a throw out of `upload.start()` can leave the registry entry behind,
      // and a stale entry makes `isResumeInFlight` refuse every later Resume for this record.
      if (itemId) unregisterUpload(itemId);
      markResumeFailed(error);
    }
  };

  /**
   * Public entry: resume using the file the user has just re-selected.
   *
   * Thin on purpose — admission control and every state mutation live in `startResumeAttempt`, so
   * that both public entries cannot drift. It clears no cancel flag; see `startResumeAttempt` for
   * why that responsibility sits in `doUpload` instead.
   */
  const resumeUploadWithFile = async (params: {
    file: File;
    persistedUpload: PersistedUpload;
    onSuccess: (item: UploadItem, mediaObject: MediaObject) => void;
    onError: (error: any) => void;
  }): Promise<ResumeOutcome> => {
    // No cancel-flag clear here either — see resumeUpload and startResumeAttempt.
    return startResumeAttempt(params);
  };

  const resumeUpload = async (params: {
    persistedUpload: PersistedUpload;
    onSuccess: (item: UploadItem, mediaObject: MediaObject) => void;
    onError: (error: any) => void;
  }): Promise<ResumeOutcome> => {
    const { persistedUpload, onSuccess, onError } = params;
    if (!persistedUpload || !persistedUpload.id) {
      onError(new Error('Invalid persisted upload object provided.'));
      return 'attempted';
    }

    // Lock first, mutations second, and hold it across the IndexedDB read — the lock startResumeAttempt
    // would otherwise take is on the far side of that await, so two triggers could both get here and
    // both wipe the row below. This function used to clear the cancel flag and wipe the row before any
    // check ran, so a duplicate trigger that was then rejected had already un-cancelled a removed
    // upload and deleted the first attempt's row, leaving it running invisibly with a dead cancel.
    const release = acquireResumeLock(persistedUpload.id);
    if (!release) return 'refused';

    // Drop any stale UploadItem (e.g. status='failed' from the previous run)
    // before the IndexedDB read window. startResumeAttempt re-publishes a
    // fresh 'pending' entry below; clearing here keeps BackgroundUploadOrchestrator
    // from re-marking the pendingUpload as failed on a poll tick that lands
    // inside the read gap.
    setUploadItems((prev) => prev.filter((u) => u.id !== persistedUpload.id));

    try {
      const fileFromCache = await getFile(persistedUpload.id);

      // Cancelled during the read? Nothing is registered yet, so `stopUpload` could not have aborted
      // anything — this check is all that stands between a removed file and a completed upload of it.
      // After the await and off a ref, because a state snapshot from before it would be stale.
      if (cancelledUploadIds.has(persistedUpload.id)) {
        Logger.info(
          '[MediaUploadContext] Resume abandoned — cancelled while reading the cached file',
          'MediaUploadContext',
          { id: persistedUpload.id },
        );
        // Reported rather than silent: the retry hook has already flipped the row back to 'uploading'
        // and spent a retry slot by this point, and nothing else would ever resolve it.
        onError(new Error('Upload was removed while preparing to resume.'));
        return 'attempted';
      }

      if (fileFromCache) {
        return await startResumeAttempt(
          {
            file: fileFromCache,
            persistedUpload,
            onSuccess,
            onError,
          },
          release,
        );
      }

      const error = new Error('File not found in local cache. Please select the file again.');
      error.name = 'FILE_NOT_IN_CACHE'; // For UI to identify this specific error
      onError(error);
      return 'attempted';
    } catch (error) {
      console.error(
        `Error during getFile in resumeUpload (IndexedDB check). ID: ${persistedUpload.id}:`,
        error,
      );
      onError(error);
      return 'attempted';
    } finally {
      // Idempotent, so this is a no-op when startResumeAttempt already released it.
      release();
    }
  };

  const value: MediaUploadContextType = {
    uploadItems,
    persistedUploads,
    isUploading,
    handleUpload,
    stopUpload,
    removePersistedUpload,
    isResumeInFlight,
    currentUploads: currentUploads.current,
    validResumableUploads,
    uploadCanBeCancelled,
    resumeUpload,
    resumeUploadWithFile,
  };

  return <MediaUploadContext.Provider value={value}>{children}</MediaUploadContext.Provider>;
};

export const useMediaUpload = (): MediaUploadContextType => {
  const context = useContext(MediaUploadContext);
  if (context === undefined) {
    throw new Error('useMediaUpload must be used within a MediaUploadProvider');
  }
  return context;
};
